import {
  ShoppingCart,
  Package,
  Tag,
  Store,
  WifiOff,
  Users,
  BarChart3,
  Layers,
  type LucideIcon,
} from 'lucide-react';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';
import { MarketingHeroPill } from '@/components/marketing/v2/MarketingHeroPill';

type Mod = { num: string; label: string; anchor: string; Icon: LucideIcon };

const MODULES: ReadonlyArray<Mod> = [
  { num: '01', label: 'Ventes', anchor: '#f1', Icon: ShoppingCart },
  { num: '02', label: 'Stock', anchor: '#f2', Icon: Package },
  { num: '03', label: 'Catalogue', anchor: '#f3', Icon: Tag },
  { num: '04', label: 'Multi-boutique', anchor: '#f4', Icon: Store },
  { num: '05', label: 'Offline', anchor: '#f5', Icon: WifiOff },
  { num: '06', label: 'Équipe', anchor: '#f6', Icon: Users },
  { num: '07', label: 'Rapports', anchor: '#f7', Icon: BarChart3 },
  { num: '08', label: 'Multi-plateforme', anchor: '#f8', Icon: Layers },
];

export default function FeaturesHeroV2() {
  return (
    <section className="relative bg-paper py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid items-end gap-12 md:grid-cols-[1.15fr_1fr] md:gap-16">
          <div>
            <MarketingHeroPill>Fonctionnalités · 8 modules</MarketingHeroPill>

            <h1 className="mt-7 font-marketing-display text-[clamp(44px,6.8vw,100px)] font-bold leading-[0.9] tracking-[-0.045em] text-charcoal-800">
              Tout ce qu'il faut
              <br />
              pour <span className="underline-lime">vendre.</span>
              <br />
              <span className="text-charcoal-400">Rien pour te ralentir.</span>
            </h1>

            <p className="mt-7 max-w-xl font-marketing-body text-lg leading-[1.55] text-charcoal-600 md:text-xl">
              Caisse, stock, catalogue, multi-boutique, mobile money, équipe, rapports,
              multi-plateforme. Un seul outil, installé en 3 minutes, construit pour le terrain
              africain.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <NeobrutalButton as="link" to="/register" variant="primary" size="lg">
                Tester les 8 modules
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M5 12h14" />
                  <path d="M13 6l6 6-6 6" />
                </svg>
              </NeobrutalButton>
              <NeobrutalButton as="link" to="/tarifs" variant="secondary" size="lg">
                Voir les tarifs
              </NeobrutalButton>
            </div>
          </div>

          {/* Right panel — 8 modules grid on charcoal */}
          <aside className="border-2 border-charcoal-800 bg-charcoal-800 p-6 shadow-neobrutal-lime-10 md:p-7">
            <div className="mb-5 flex items-center justify-between font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-lime-400">
              <span className="inline-flex items-center gap-2">
                <span className="h-px w-7 bg-lime-500" />
                Modules inclus
              </span>
              <span className="text-charcoal-200">Tous les plans</span>
            </div>

            <ul className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
              {MODULES.map(({ num, label, anchor, Icon }) => (
                <li key={num}>
                  <a
                    href={anchor}
                    className="group relative flex h-full flex-col justify-between gap-4 border-2 border-charcoal-700 bg-charcoal-700 p-3 transition-all duration-150 hover:border-lime-500 hover:bg-charcoal-600 md:p-4"
                  >
                    <Icon
                      className="h-5 w-5 text-lime-400 transition-colors group-hover:text-lime-300"
                      strokeWidth={2}
                    />
                    <div>
                      <div className="font-marketing-display text-[14px] font-bold leading-none text-white md:text-[15px]">
                        {label}
                      </div>
                      <div className="mt-1 font-marketing-mono text-[10px] font-medium uppercase tracking-[0.12em] text-charcoal-200">
                        {num}
                      </div>
                    </div>
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-5 flex items-center justify-between border-t-2 border-charcoal-700 pt-4 font-marketing-mono text-[10px] font-medium uppercase tracking-[0.12em] text-charcoal-200">
              <span>
                Dès le plan <b className="text-white">Starter</b>
              </span>
              <span>FR · EN</span>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
